import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { onError } from '@apollo/client/link/error'
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, fromPromise } from '@apollo/client'
import { useTokenStore } from '@/src/common/store/useTokenStore'
import { graphql } from '@api/mesa'
import type { RefreshAccessTokenMutationVariables } from '@api/mesa/graphql'
import { graphqlEndpoint } from '@/src/common/gql/graphqlEndpoint'
import { getMesaApiRequestHeaders } from '@/src/common/utils/getMesaApiRequestHeaders'
import { stringifyMessage } from '@/src/common/hooks/useLogError/stringifyMessage'
import * as Sentry from '@sentry/react-native'
import { serializeError } from 'serialize-error'
import { sentryCaptureException } from '@/src/common/hooks/useLogError'
import { z } from 'zod/v4'

if (process.env.EXPO_PUBLIC_ENVIRONMENT === 'dev' || !process.env.EXPO_PUBLIC_ENVIRONMENT) {
  loadDevMessages()
  loadErrorMessages()
}

export async function refreshAccessToken(variables: RefreshAccessTokenMutationVariables) {
  const client = new ApolloClient({ uri: graphqlEndpoint, cache: new InMemoryCache() })

  const { data } = await client.mutate({
    mutation: graphql(`
      mutation RefreshAccessToken($refreshToken: String!) {
        refreshToken(refreshToken: $refreshToken) {
          accessToken
          tokenType
          expiresIn
        }
      }
    `),
    variables,
  })

  return data
}

const LegacyGraphqlExtensions = z
  .object({ originalError: z.object({ status: z.number() }) })
  .passthrough()

const errorLink = onError(({ graphQLErrors, operation, forward, networkError, response }) => {
  const data = {
    graphQLErrors: graphQLErrors?.map((e) => serializeError(e)),
    operation,
    networkError: serializeError(networkError),
    response,
  } as const

  // Custom filter logic: only alert if there's
  // - at least one graphql error with a 500+ code in extensions
  graphQLErrors?.filter((err) => {
    const statusCode =
      LegacyGraphqlExtensions.safeParse(err.extensions).data?.originalError.status || 0
    return statusCode >= 500
  }).length ||
  // - a network error with a 500+ status code (an unknown internal server error)
  (networkError &&
    'statusCode' in networkError &&
    typeof networkError.statusCode === 'number' &&
    networkError.statusCode >= 500)
    ? sentryCaptureException('Error in Apollo Client', data)
    : Sentry.addBreadcrumb({
        message: 'GraphQl Request Error',
        data,
        level: 'info',
      })

  // there are no errors, and we've already logged what we could, so there's no fallback behavior left for us to do
  if (!graphQLErrors) return

  const isUnauthenticated = graphQLErrors.some((err) => err.extensions?.code === 'UNAUTHENTICATED')

  // if the error is not unauthenticated, there's no fallback behavior left for us to do
  if (!isUnauthenticated) return

  const { tokens, setEntireResponse, clearTokens } = useTokenStore.getState()

  if (!tokens) return

  const refreshToken = 'refreshToken' in tokens ? tokens.refreshToken : undefined

  // there's no refresh token for us to refresh the access token with, so we can't do anything
  if (!refreshToken) return

  return fromPromise(
    (async () => {
      try {
        const res = await refreshAccessToken({ refreshToken })

        const accessToken = res?.refreshToken.accessToken
        if (!accessToken) {
          throw new Error('Failed to obtain new access token')
        }

        setEntireResponse(res.refreshToken)
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            ...getMesaApiRequestHeaders(),
            authorization: `Bearer ${accessToken}`,
          },
        }))
        return accessToken
      } catch (error) {
        clearTokens()
        console.error('Error refreshing access token:', stringifyMessage(error))
        throw error
      }
    })(),
  ).flatMap(() => forward(operation))
})

const urlLink = new HttpLink({ uri: graphqlEndpoint })

const headersLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: { ...headers, ...getMesaApiRequestHeaders() },
  }))
  return forward(operation)
})

/** Notes: Even with a refresh token not existing, if access token was generated less than 24 hours ago (`AuthTokenResponseDto.expiresIn` is 86400, i.e. 24 hours), it will work */
export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    errorLink,
    headersLink,
    // urlLink must appear last because it's the link that sends the http request. any links after urlLink will not be applied
    urlLink,
  ]),
  cache: new InMemoryCache(),
})
